import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { mailHref, readMailState } from "@/lib/mailUrlState";
import { AtSign, Calendar, Clock, Filter, Flag, Mail, Paperclip, Search, SlidersHorizontal, User, X } from "@fg/icons";
import { mailApi, type Contact } from "@/api/mailApi";
import { useFolders } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDismiss } from "@/lib/useDismiss";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { addRecentSearch, loadRecentSearches, removeRecentSearch } from "@/lib/recentSearches";
import {
    activePeopleToken,
    buildQueryFromForm,
    DATE_WITHIN_OPTIONS,
    emptyAdvancedSearch,
    formFromQuery,
    serializeToken,
    serializeTokens,
    tokenizeQuery,
    toIsoDate,
    type AdvancedSearch,
    type DateWithin
} from "@/lib/searchQuery";

type Suggestion =
    | { kind: "person"; contact: Contact; replace: "token" | "word" }
    | { kind: "recent"; query: string };

/** Date chips under "Any time" → an `after:` operator. */
const DATE_CHIPS: Array<{ label: string; days: number }> = [
    { label: "Past day", days: 1 },
    { label: "Past week", days: 7 },
    { label: "Past month", days: 30 },
    { label: "Past year", days: 365 }
];

/** The plain word at the caret (no operator) — people are suggested for it too. */
function activeFreeWord(q: string, caret: number): { value: string; start: number; end: number } | null {
    const at = Math.min(caret, q.length);
    const start = q.lastIndexOf(" ", at - 1) + 1;
    const next = q.indexOf(" ", at);
    const end = next === -1 ? q.length : next;
    const word = q.slice(start, end);
    if (word.length < 2 || word.includes(":") || word.startsWith("-") || word.startsWith('"')) return null;
    return { value: word, start, end };
}

/**
 * Top-bar search with Gmail operators (the BFF parses them). On focus a
 * flyout offers filter chips that insert operators (From, To me, Has
 * attachment, Unread, Flagged, Any time, Advanced), recent searches and
 * people from the contacts autocomplete. The funnel opens the advanced-search
 * form, which writes the same query.
 */
export function SearchBox({
    searchInputRef,
    onOpenPalette
}: {
    searchInputRef?: React.RefObject<HTMLInputElement>;
    onOpenPalette?: () => void;
}) {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const urlQuery = readMailState(params).q ?? "";
    const token = useAccessToken();
    const auth = useAuth();
    const myEmail = auth.user?.profile.email ?? "";
    const listId = useId();

    const [value, setValue] = useState(urlQuery);
    // Chips remove operators by rewriting the URL — follow it.
    useEffect(() => setValue(urlQuery), [urlQuery]);

    const [open, setOpen] = useState(false);
    const [caret, setCaret] = useState(0);
    const [recent, setRecent] = useState<string[]>(loadRecentSearches);
    const [people, setPeople] = useState<Contact[]>([]);
    const [active, setActive] = useState(-1);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [datesOpen, setDatesOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    const peopleToken = useMemo(() => activePeopleToken(value, caret), [value, caret]);
    const freeWord = useMemo(() => (peopleToken ? null : activeFreeWord(value, caret)), [peopleToken, value, caret]);
    const lookup = peopleToken ? peopleToken.value.trim() : freeWord?.value ?? "";

    // People suggestions for `from:` / `to:` and for a plain name being typed (existing contacts autocomplete).
    useEffect(() => {
        if (lookup.length === 0) {
            setPeople([]);
            return;
        }
        let cancelled = false;
        const handle = setTimeout(() => {
            mailApi.autocompleteContacts(lookup, token)
                .then((list) => { if (!cancelled) setPeople(list.slice(0, 6)); })
                .catch(() => { if (!cancelled) setPeople([]); });
        }, 200);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [lookup, token]);

    const suggestions: Suggestion[] = useMemo(() => {
        if (peopleToken) return people.map((contact) => ({ kind: "person" as const, contact, replace: "token" as const }));
        const needle = value.trim().toLowerCase();
        const recents = recent
            .filter((q) => !needle || (q.toLowerCase().includes(needle) && q.toLowerCase() !== needle))
            .slice(0, 5)
            .map((query) => ({ kind: "recent" as const, query }));
        const persons = freeWord
            ? people.slice(0, 4).map((contact) => ({ kind: "person" as const, contact, replace: "word" as const }))
            : [];
        return [...recents, ...persons];
    }, [peopleToken, freeWord, people, recent, value]);

    const showList = open && suggestions.length > 0;
    // The flyout: chips always (unless picking a person for from:/to:), plus the suggestions.
    const showFlyout = open && (!peopleToken || suggestions.length > 0);
    useEffect(() => setActive(-1), [suggestions.length, peopleToken?.key]);

    const focusAt = (pos: number) => {
        requestAnimationFrame(() => {
            const input = searchInputRef?.current;
            if (!input) return;
            input.focus();
            input.setSelectionRange(pos, pos);
            setCaret(pos);
        });
    };

    const submit = (raw: string) => {
        const q = raw.trim();
        setOpen(false);
        setAdvancedOpen(false);
        setDatesOpen(false);
        if (!q) {
            navigate("/mail");
            return;
        }
        setRecent(addRecentSearch(q));
        navigate(mailHref({ q }));
        searchInputRef?.current?.blur();
    };

    /** Adds an operator to the query (once), e.g. "is:unread"; `replaceKey` drops an existing one first. */
    const addOperator = (operator: string, replaceKey?: string) => {
        let tokens = tokenizeQuery(value);
        if (replaceKey) tokens = tokens.filter((t) => t.key !== replaceKey);
        const base = serializeTokens(tokens);
        const already = tokens.some((t) => serializeToken(t).toLowerCase() === operator.toLowerCase());
        const joined = already ? base : base ? `${base} ${operator}` : operator;
        // "from:" waits for a name (the people list opens); complete operators get a trailing space.
        const next = operator.endsWith(":") && !already ? joined : `${joined} `;
        setValue(next);
        setOpen(true);
        focusAt(next.length);
    };

    const choose = (s: Suggestion) => {
        if (s.kind === "recent") {
            setValue(s.query);
            submit(s.query);
            return;
        }
        const email = s.contact.emailAddress;
        let prefix: string;
        let rest: string;
        let insert: string;
        if (s.replace === "token" && peopleToken) {
            prefix = value.slice(0, peopleToken.start);
            const negated = value.slice(peopleToken.start, peopleToken.end).startsWith("-") ? "-" : "";
            insert = `${negated}${peopleToken.key}:${email} `;
            rest = value.slice(peopleToken.end).trimStart();
        } else if (s.replace === "word" && freeWord) {
            prefix = value.slice(0, freeWord.start);
            insert = `from:${email} `;
            rest = value.slice(freeWord.end).trimStart();
        } else {
            return;
        }
        const next = `${prefix}${insert}${rest}`;
        setValue(next);
        setPeople([]);
        focusAt(prefix.length + insert.length);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown" && suggestions.length > 0) {
            e.preventDefault();
            setOpen(true);
            setActive((i) => (i + 1) % suggestions.length);
            return;
        }
        if (e.key === "ArrowUp" && suggestions.length > 0) {
            e.preventDefault();
            setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
            return;
        }
        if (e.key === "Enter" && showList && active >= 0) {
            e.preventDefault();
            const s = suggestions[active];
            if (s) choose(s);
            return;
        }
        if (e.key === "Escape") {
            if (showFlyout) {
                setOpen(false);
                return;
            }
            setValue("");
            submit("");
            (e.target as HTMLInputElement).blur();
        }
    };

    const recentCount = suggestions.filter((s) => s.kind === "recent").length;

    return (
        <div ref={wrapRef} className="relative w-full max-w-xl">
            <form
                role="search"
                onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    submit(value);
                }}
                onBlur={(e) => {
                    if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) {
                        setOpen(false);
                        setDatesOpen(false);
                    }
                }}
            >
                <Search className="mail-appbar-input-muted pointer-events-none absolute left-2.5 top-4 h-4 w-4 -translate-y-1/2" aria-hidden />
                <Input
                    ref={searchInputRef}
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setCaret(e.target.selectionStart ?? e.target.value.length);
                        setOpen(true);
                    }}
                    onSelect={(e) => setCaret((e.target as HTMLInputElement).selectionStart ?? 0)}
                    onFocus={() => {
                        setRecent(loadRecentSearches());
                        setOpen(true);
                    }}
                    onClick={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Search mail (/)"
                    aria-label="Search mail. Supports from:, to:, subject:, has:attachment, is:unread, in:, before:, after:, larger:"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showList}
                    aria-controls={listId}
                    aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
                    autoComplete="off"
                    spellCheck={false}
                    className="mail-appbar-input h-8 border-0 pl-9 pr-16 md:pr-24"
                />
                <div className="absolute right-1 top-4 flex -translate-y-1/2 items-center gap-0.5">
                    {value ? (
                        <button
                            type="button"
                            onClick={() => {
                                setValue("");
                                if (urlQuery) submit("");
                                searchInputRef?.current?.focus();
                            }}
                            aria-label="Clear search"
                            title="Clear search"
                            className="mail-appbar-input-btn flex h-6 w-6 items-center justify-center rounded"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    ) : onOpenPalette ? (
                        <button
                            type="button"
                            onClick={onOpenPalette}
                            aria-label="Open the command palette (Ctrl+K)"
                            title="Command palette (Ctrl+K)"
                            className="mail-appbar-input-btn hidden h-6 items-center rounded border px-1.5 font-mono text-[11px] md:flex"
                        >
                            Ctrl K
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            setAdvancedOpen((o) => !o);
                        }}
                        aria-label="Show search options"
                        title="Show search options"
                        aria-haspopup="dialog"
                        aria-expanded={advancedOpen}
                        className="mail-appbar-input-btn flex h-6 w-6 items-center justify-center rounded"
                    >
                        <Filter className="h-3.5 w-3.5" />
                    </button>
                </div>

                {showFlyout ? (
                    <div
                        role="dialog"
                        aria-label="Search suggestions"
                        className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-auto rounded-lg border bg-popover p-2 text-sm text-popover-foreground shadow-xl"
                    >
                        {!peopleToken ? (
                            <div role="group" aria-label="Search filters" className="flex flex-wrap gap-1.5 p-1">
                                <Chip icon={<User className="h-3.5 w-3.5" />} onPick={() => addOperator("from:")}>From</Chip>
                                <Chip icon={<AtSign className="h-3.5 w-3.5" />} onPick={() => addOperator(myEmail ? `to:${myEmail}` : "to:")}>To me</Chip>
                                <Chip icon={<Paperclip className="h-3.5 w-3.5" />} onPick={() => addOperator("has:attachment")}>Has attachment</Chip>
                                <Chip icon={<Mail className="h-3.5 w-3.5" />} onPick={() => addOperator("is:unread")}>Unread</Chip>
                                <Chip icon={<Flag className="h-3.5 w-3.5" />} onPick={() => addOperator("is:flagged")}>Flagged</Chip>
                                <Chip
                                    icon={<Calendar className="h-3.5 w-3.5" />}
                                    expanded={datesOpen}
                                    onPick={() => setDatesOpen((o) => !o)}
                                >
                                    Any time
                                </Chip>
                                <Chip
                                    icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
                                    onPick={() => {
                                        setOpen(false);
                                        setAdvancedOpen(true);
                                    }}
                                >
                                    Advanced
                                </Chip>
                                {datesOpen ? (
                                    <div role="group" aria-label="Date range" className="flex w-full flex-wrap gap-1.5 pt-0.5">
                                        {DATE_CHIPS.map((d) => (
                                            <Chip
                                                key={d.days}
                                                subtle
                                                onPick={() => {
                                                    const since = new Date();
                                                    since.setDate(since.getDate() - d.days);
                                                    setDatesOpen(false);
                                                    addOperator(`after:${toIsoDate(since)}`, "after");
                                                }}
                                            >
                                                {d.label}
                                            </Chip>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {suggestions.length > 0 ? (
                            <ul id={listId} role="listbox" aria-label={peopleToken ? "People" : "Suggestions"} className="mt-1 py-0.5">
                                {suggestions.map((s, i) => (
                                    <SuggestionRow
                                        key={s.kind === "person" ? `p-${s.contact.id}` : `r-${s.query}`}
                                        heading={
                                            i === 0 && s.kind === "recent" ? "Recent searches"
                                                : s.kind === "person" && (i === 0 || i === recentCount) ? "People"
                                                    : null
                                        }
                                        id={`${listId}-${i}`}
                                        selected={i === active}
                                        onPick={() => choose(s)}
                                        onHover={() => setActive(i)}
                                    >
                                        {s.kind === "person" ? (
                                            <>
                                                <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                                <span className="min-w-0 truncate">
                                                    <span className="font-medium">{s.contact.displayName || s.contact.emailAddress}</span>
                                                    {s.contact.displayName ? (
                                                        <span className="text-muted-foreground"> &lt;{s.contact.emailAddress}&gt;</span>
                                                    ) : null}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                                <span className="min-w-0 flex-1 truncate">{s.query}</span>
                                                <button
                                                    type="button"
                                                    tabIndex={-1}
                                                    aria-label={`Remove ${s.query} from recent searches`}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setRecent(removeRecentSearch(s.query));
                                                    }}
                                                    className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </SuggestionRow>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                ) : null}
            </form>

            {advancedOpen ? (
                <AdvancedSearchPanel
                    dismissRef={wrapRef}
                    initial={formFromQuery(value)}
                    onClose={() => setAdvancedOpen(false)}
                    onSearch={(q) => {
                        setValue(q);
                        submit(q);
                    }}
                />
            ) : null}
        </div>
    );
}

/** A filter chip. Mouse-down doesn't steal focus from the search box (so the flyout stays open). */
function Chip({
    icon,
    subtle,
    expanded,
    onPick,
    children
}: {
    icon?: ReactNode;
    subtle?: boolean;
    expanded?: boolean;
    onPick: () => void;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            aria-expanded={expanded}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onPick}
            className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded border px-2.5 text-[13px] text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                subtle ? "border-dashed bg-transparent" : "bg-background",
                expanded && "bg-accent"
            )}
        >
            {icon ? <span className="text-muted-foreground" aria-hidden>{icon}</span> : null}
            {children}
        </button>
    );
}

function SuggestionRow({
    heading,
    id,
    selected,
    onPick,
    onHover,
    children
}: {
    heading: string | null;
    id: string;
    selected: boolean;
    onPick: () => void;
    onHover: () => void;
    children: ReactNode;
}) {
    return (
        <>
            {heading ? (
                <li role="presentation" className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {heading}
                </li>
            ) : null}
            <li
                id={id}
                role="option"
                aria-selected={selected}
                // mousedown so the input doesn't blur (closing the list) first
                onMouseDown={(e) => {
                    e.preventDefault();
                    onPick();
                }}
                onMouseEnter={onHover}
                className={cn(
                    "group flex cursor-pointer items-center gap-2 rounded px-2 py-1.5",
                    selected ? "bg-accent text-accent-foreground" : "hover:bg-accent/60"
                )}
            >
                {children}
            </li>
        </>
    );
}

function AdvancedSearchPanel({
    dismissRef,
    initial,
    onClose,
    onSearch
}: {
    /** Clicks inside this element (the search box + funnel) don't dismiss — the funnel toggles. */
    dismissRef: React.RefObject<HTMLDivElement>;
    initial: AdvancedSearch;
    onClose: () => void;
    onSearch: (query: string) => void;
}) {
    const [form, setForm] = useState<AdvancedSearch>(initial);
    const panelRef = useRef<HTMLDivElement>(null);
    const folders = useFolders();
    useDismiss(dismissRef, onClose);
    useFocusTrap(panelRef, true);
    const set = <K extends keyof AdvancedSearch>(key: K, v: AdvancedSearch[K]) => setForm((f) => ({ ...f, [key]: v }));
    const preview = buildQueryFromForm(form);
    const idp = useId();

    const field = "h-8 w-full min-w-0 rounded-md border bg-background px-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";
    const row = "grid grid-cols-1 items-center gap-1 sm:grid-cols-[8.5rem_1fr] sm:gap-3";
    const label = "text-xs font-medium text-muted-foreground sm:text-right";

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label="Advanced search"
            className="fixed inset-x-2 top-14 z-50 max-h-[80vh] overflow-y-auto rounded-lg border bg-popover p-4 text-popover-foreground shadow-xl sm:absolute sm:inset-x-auto sm:left-0 sm:right-0 sm:top-full sm:mt-1"
        >
            <form
                className="space-y-2.5"
                onSubmit={(e) => {
                    e.preventDefault();
                    onSearch(preview);
                }}
            >
                <div className={row}>
                    <label htmlFor={`${idp}-from`} className={label}>From</label>
                    <input id={`${idp}-from`} className={field} value={form.from} onChange={(e) => set("from", e.target.value)} placeholder="name or address" />
                </div>
                <div className={row}>
                    <label htmlFor={`${idp}-to`} className={label}>To</label>
                    <input id={`${idp}-to`} className={field} value={form.to} onChange={(e) => set("to", e.target.value)} placeholder="name or address" />
                </div>
                <div className={row}>
                    <label htmlFor={`${idp}-subject`} className={label}>Subject</label>
                    <input id={`${idp}-subject`} className={field} value={form.subject} onChange={(e) => set("subject", e.target.value)} />
                </div>
                <div className={row}>
                    <label htmlFor={`${idp}-has`} className={label}>Has the words</label>
                    <input id={`${idp}-has`} className={field} value={form.hasWords} onChange={(e) => set("hasWords", e.target.value)} placeholder='words or "exact phrase"' />
                </div>
                <div className={row}>
                    <label htmlFor={`${idp}-not`} className={label}>Doesn&apos;t have</label>
                    <input id={`${idp}-not`} className={field} value={form.doesntHave} onChange={(e) => set("doesntHave", e.target.value)} />
                </div>
                <div className={row}>
                    <span className={label} id={`${idp}-size-label`}>Size</span>
                    <div className="flex min-w-0 gap-2" role="group" aria-labelledby={`${idp}-size-label`}>
                        <select aria-label="Size comparison" className={cn(field, "w-auto")} value={form.sizeOp} onChange={(e) => set("sizeOp", e.target.value as AdvancedSearch["sizeOp"])}>
                            <option value="larger">greater than</option>
                            <option value="smaller">less than</option>
                        </select>
                        <input aria-label="Size" inputMode="decimal" className={cn(field, "w-20 flex-1")} value={form.size} onChange={(e) => set("size", e.target.value.replace(/[^\d.]/g, ""))} />
                        <select aria-label="Size unit" className={cn(field, "w-auto")} value={form.sizeUnit} onChange={(e) => set("sizeUnit", e.target.value as AdvancedSearch["sizeUnit"])}>
                            <option value="M">MB</option>
                            <option value="K">KB</option>
                            <option value="B">Bytes</option>
                        </select>
                    </div>
                </div>
                <div className={row}>
                    <span className={label} id={`${idp}-date-label`}>Date within</span>
                    <div className="flex min-w-0 gap-2" role="group" aria-labelledby={`${idp}-date-label`}>
                        <select aria-label="Date range" className={cn(field, "w-auto")} value={form.dateWithin} onChange={(e) => set("dateWithin", e.target.value as DateWithin)}>
                            {DATE_WITHIN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input
                            type="date"
                            aria-label="Of date"
                            className={cn(field, "flex-1")}
                            value={form.dateAnchor}
                            disabled={!form.dateWithin}
                            onChange={(e) => set("dateAnchor", e.target.value)}
                        />
                    </div>
                </div>
                <div className={row}>
                    <label htmlFor={`${idp}-folder`} className={label}>Search</label>
                    <select id={`${idp}-folder`} className={field} value={form.folder} onChange={(e) => set("folder", e.target.value)}>
                        <option value="">All mail</option>
                        <option value="anywhere">All mail incl. Spam &amp; Trash</option>
                        {(folders.data ?? []).map((f) => (
                            <option key={f.id} value={f.id}>{"  ".repeat(f.depth ?? 0)}{f.name}</option>
                        ))}
                        {form.folder && form.folder !== "anywhere" && !(folders.data ?? []).some((f) => f.id === form.folder)
                            ? <option value={form.folder}>{form.folder}</option>
                            : null}
                    </select>
                </div>
                <div className={row}>
                    <span className="hidden sm:block" />
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="h-4 w-4 accent-primary" checked={form.hasAttachment} onChange={(e) => set("hasAttachment", e.target.checked)} />
                        Has attachment
                    </label>
                </div>
                {form.extra ? (
                    <p className="text-xs text-muted-foreground sm:pl-[9.25rem]">
                        Also keeps: <code className="rounded bg-muted px-1">{form.extra}</code>
                    </p>
                ) : null}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                    <code className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={preview} aria-label="Query preview">
                        {preview || "—"}
                    </code>
                    <div className="flex gap-2">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setForm(emptyAdvancedSearch())}>
                            Clear filter
                        </Button>
                        <Button type="submit" size="sm" disabled={!preview}>
                            Search
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
